<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="mb-2">Dashboard</h1>
        <p class="text-body1 text-medium-emphasis">Manage your gigs and staff bookings</p>
      </v-col>
    </v-row>

    <v-row>
      <!-- Quick Actions -->
      <v-col cols="12" md="3">
        <v-card class="h-100" :to="{ name: 'GigsList' }">
          <v-card-item>
            <template #title>
              <v-icon start size="large">mdi-briefcase</v-icon>
              Gigs
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-h6">{{ gigCount }}</p>
            <p class="text-body2 text-medium-emphasis">Total gigs</p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="h-100">
          <v-card-item>
            <template #title>
              <v-icon start size="large">mdi-account-multiple</v-icon>
              Team
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-h6">—</p>
            <p class="text-body2 text-medium-emphasis">Coming soon</p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="h-100">
          <v-card-item>
            <template #title>
              <v-icon start size="large">mdi-calendar-check</v-icon>
              Today
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-h6">{{ todayCount }}</p>
            <p class="text-body2 text-medium-emphasis">Calls scheduled</p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="h-100">
          <v-card-item>
            <template #title>
              <v-icon start size="large">mdi-alert-circle</v-icon>
              Pending
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-h6">0</p>
            <p class="text-body2 text-medium-emphasis">Offers to respond</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upcoming Gigs -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-card-item>
            <template #title>
              Upcoming Gigs
              <v-btn text size="small" :to="{ name: 'GigsList' }" class="ml-2">View All</v-btn>
            </template>
          </v-card-item>

          <v-card-text v-if="upcomingGigs.length === 0">
            <p class="text-body2 text-medium-emphasis">No upcoming gigs</p>
          </v-card-text>

          <v-list v-else>
            <v-list-item v-for="gig in upcomingGigs" :key="gig.id" :to="`/gigs/${gig.id}`">
              <v-list-item-title>{{ gig.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDate(gig.date) }} • {{ gig.state }}
              </v-list-item-subtitle>
              <template #append>
                <v-chip size="small" :color="stateColor(gig.state)">{{ gig.state }}</v-chip>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useGigsStore } from '../stores/gigs';

const route = useRoute();
const gigsStore = useGigsStore();

const gigs = computed(() => gigsStore.gigs);
const gigCount = computed(() => gigs.value.length);

const todayCount = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return gigs.value.filter(g => {
    const gigDate = new Date(g.date);
    gigDate.setHours(0, 0, 0, 0);
    return gigDate.getTime() === today.getTime();
  }).length;
});

const upcomingGigs = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return [...gigs.value]
    .filter(g => new Date(g.date) >= today)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);
});

onMounted(async () => {
  const orgId = route.query.org as string;
  if (orgId && gigs.value.length === 0) {
    await gigsStore.loadOrgGigs(orgId);
  }
});

function formatDate(date: any): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function stateColor(state: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'grey',
    QUOTED: 'blue',
    BOOKED: 'green',
    COMPLETED: 'purple',
    INVOICED: 'orange',
    PAID: 'success',
    CANCELLED: 'red',
  };
  return colors[state] || 'grey';
}
</script>
